import { default } from './surveyItem.vue';
<script setup lang="ts">
  import { Sh } from '@/core/sharedService';
import { DisplayType, FormDesignItemDto, PropertyDisplayType } from '@/core/store/survey/surveyDesignDtos';
  import { SurveyResponseItemDto } from '@/core/store/survey/surveyResponseDtos';
  import Survey from '@/core/store/survey/surveyStore';
  import { computed, CSSProperties, PropType, ref, watch, onMounted, nextTick, onBeforeUnmount } from 'vue';
  const {
      displayMode, 
      isDesign,
      } = Survey();

   const props  = defineProps({
    item: {
      type: Object as () => FormDesignItemDto,
      required: true
    },
    response: {
      type: Object as () => SurveyResponseItemDto,
      default: null,
      required: false
    },
    onChange: {
      type: Function as PropType<( value: any) => void>,
      default: null,
      required: false,
    },
  });

  const currentStyles = computed<PropertyDisplayType>(()=>{
      switch(displayMode.value){
        case DisplayType.DISPLAY: 
          return  props.item.properties.screen;
        case DisplayType.EDIT: 
          return  props.item.properties.input;
        case DisplayType.PDF: 
          return  props.item.properties.print;
      }
  });

  const cssInput = computed<CSSProperties>(() => {
    const displayProp: PropertyDisplayType=  currentStyles.value;
    return {
      backgroundColor:  displayProp.contentStyle.backgroundColor,
      borderRadius: isMultiline.value ? '5px' : '9999px',
      border: 'none',
      fontSize: Sh.getFontSize(displayProp.contentStyle.fontSize),
      color: displayProp.contentStyle.color,
      width: isDesign.value && displayMode.value === DisplayType.EDIT ? '344px' : '100%',
      minHeight: `${lineHeight}px`,
      overflow: 'hidden',
      resize: 'none',
      padding: '2px 12px',
      lineHeight: `${lineHeight}px`,
    }
  });

  watch(() => props.response?.value, (newValue) => {
    if (newValue !== undefined) {
      value.value = newValue;
      nextTick(() => {
        autoResize();
      });
    }
  })

  const value = ref<string>(props.response && props.response.value ? props.response.value : '');

  
  watch(value, (newValue) => {
    if(props.response){
      if(newValue !== props.response.value){
        props.response.value = newValue;
        props.onChange(newValue);
      }
    }
  });
  watch(displayMode , (newState) => {
    if(newState == DisplayType.EDIT){
      nextTick(() => {
        autoResize();
      })
      
    }
  });
  const isCurrentVisible = computed(() => {
    const displayProp: PropertyDisplayType=  currentStyles.value;
    return displayProp.visible || isDesign;
  });

  const lineHeight = 30;
  const isMultiline = ref(false);

  const textareaRef = ref(null);
  let intersectionObs: IntersectionObserver | null = null;
  let resizeObs: ResizeObserver | null = null;
  let mutationObs: MutationObserver | null = null;
  const autoResize = (retry = 0) => {
    const textarea = textareaRef.value as HTMLTextAreaElement | null;
    if (!textarea) return;

    // Reset height so scrollHeight will reflect content
    textarea.style.height = 'auto';

    requestAnimationFrame(() => {
      // If the element has no width yet (still in a hidden/folding container), retry a few times
      if ((textarea as HTMLElement).offsetWidth === 0) {
        if (retry < 6) {
          requestAnimationFrame(() => autoResize(retry + 1));
        }
        return;
      }

      // Use scrollHeight directly. Add a small fudge to avoid cropping on some browsers.
      const measured = textarea.scrollHeight || 0;

      if (measured <= 0 && retry < 6) {
        requestAnimationFrame(() => autoResize(retry + 1));
        return;
      }

      const extra = 2; // small extra px to account for rounding
  const newHeight = Math.max(lineHeight, measured + extra);
  // Prevent external CSS from clipping the textarea (some global rules set max-height)
  textarea.style.maxHeight = 'none';
  textarea.style.overflow = 'hidden';
      isMultiline.value = newHeight > lineHeight + 1;
      textarea.style.height = `${newHeight}px`;
    });
  };

  // wrapper for the textarea input event to avoid TypeScript event/parameter mismatch
  const onInput = (e: Event) => {
    // keep default behavior and recalc size
    autoResize();
  };


  onMounted(() => {
    nextTick(() => {
      autoResize();
    });
    // observe visibility/size changes so we can recalc when the textarea becomes visible
    const observe = () => {
      const el = textareaRef.value as HTMLElement | null;
      if (!el) return;

      // IntersectionObserver: when element enters viewport (or becomes visible)
      try {
        if ('IntersectionObserver' in window) {
          intersectionObs = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                nextTick(() => autoResize());
              }
            });
          });
          intersectionObs.observe(el);
        }
      } catch (e) {
        // ignore
      }

      // ResizeObserver: parent/container size changes
      try {
        if ('ResizeObserver' in window) {
          resizeObs = new ResizeObserver(() => {
            nextTick(() => autoResize());
          });
          resizeObs.observe(el);
        }
      } catch (e) {}

      // MutationObserver: watch attribute changes on ancestors (class/style) as a fallback
      try {
        const target = el.parentElement || el;
        mutationObs = new MutationObserver(() => {
          // if element now has size, recalc
          if ((el as HTMLElement).offsetWidth > 0 && (el as HTMLElement).offsetHeight > 0) {
            nextTick(() => autoResize());
          }
        });
        mutationObs.observe(target, { attributes: true, attributeFilter: ['style', 'class'] });
      } catch (e) {}
    };

    observe();
  });

  onBeforeUnmount(() => {
    if (intersectionObs) { intersectionObs.disconnect(); intersectionObs = null; }
    if (resizeObs) { resizeObs.disconnect(); resizeObs = null; }
    if (mutationObs) { mutationObs.disconnect(); mutationObs = null; }
  });

  // When visibility changes (controlled by styles/properties) recalc height
  watch(isCurrentVisible, (visible) => {
    if (visible) {
      nextTick(() => autoResize());
    }
  });

  watch(value, () => {
    nextTick(() => {
      autoResize();
    });
  });

  watch(() => props.response?.value, (newValue) => {
      if (newValue !== undefined) {
        value.value = newValue;
        nextTick(() => {
          autoResize();
        });
      }
  });

</script>
<template>
  <div >
  <textarea v-model="value" @input="onInput" ref="textareaRef" class="survey-item-input survey-input-number-buttons-none" :readonly="displayMode!=DisplayType.EDIT || (item.inheritItemId!=null && item.inheritItemId.trim().length!=0)" :tabindex="displayMode!=DisplayType.EDIT ? -1 : 1" :style="cssInput"></textarea>
  </div>  
  <!-- <div v-if="displayMode == DisplayType.PDF  && isCurrentVisible" class="survey-item-input survey-input-number-buttons-none survey-item-input-disabled" v-html="value.replace(/\n/g, '<br>')">
  </div>   -->
</template>

<style lang="scss">
  @import url(../survey.scss);
</style>

<script lang="ts">
  export default {
    name: "SurveyItemText"
  }
</script>